/**
 * Multi-Stakeholder Interface
 * Collaborative project planning interface with shared decision-making tools
 * Supports real-time collaboration for VRP optimization projects
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  MessageSquare,
  Vote,
  UserPlus,
  Bell,
  Clock,
  Eye,
  MapPin,
  Truck,
  BarChart3,
  Filter,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  MoreHorizontal,
} from 'lucide-react';

// Enhanced UI Components
import { BentoGrid, BentoGridItem } from '../common/BentoGrid';
import { MagicCard } from '../ui/magic/MagicCard';
import { NumberTicker } from '../ui/magic/NumberTicker';
import { cn } from '../../lib/utils';

// Context and hooks
import { useProject } from '../../contexts/ProjectContext';

// Types
interface Stakeholder {
  id: string;
  name: string;
  role: 'project_manager' | 'fleet_manager' | 'route_planner' | 'analyst' | 'client' | 'admin';
  email: string;
  avatar?: string;
  status: 'online' | 'offline' | 'away';
  lastSeen: Date;
  permissions: string[];
}

interface Decision {
  id: string;
  title: string;
  description: string;
  type: 'route_optimization' | 'vehicle_assignment' | 'schedule_change' | 'cost_approval' | 'policy_change';
  status: 'pending' | 'voting' | 'approved' | 'rejected' | 'implemented';
  createdBy: string;
  createdAt: Date;
  deadline: Date;
  votes: Vote[];
  comments: Comment[];
  requiredApprovals: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
}

interface Vote {
  stakeholderId: string;
  decision: 'approve' | 'reject' | 'abstain';
  comment?: string;
  timestamp: Date;
}

interface Comment {
  id: string;
  stakeholderId: string;
  content: string;
  timestamp: Date;
  replies?: Comment[];
}

interface Notification {
  id: string;
  type: 'decision_pending' | 'vote_required' | 'comment_added' | 'deadline_approaching' | 'status_change';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionRequired: boolean;
}

interface StakeholderInterfaceProps {
  className?: string;
  currentUserId?: string;
  showNotifications?: boolean;
  enableRealTimeUpdates?: boolean;
}

// Mock data for demonstration
const mockStakeholders: Stakeholder[] = [
  {
    id: '1',
    name: 'Sarah Johnson',
    role: 'project_manager',
    email: 'sarah.johnson@company.com',
    status: 'online',
    lastSeen: new Date(),
    permissions: ['create_decisions', 'manage_stakeholders', 'view_all'],
  },
  {
    id: '2',
    name: 'Mike Chen',
    role: 'fleet_manager',
    email: 'mike.chen@company.com',
    status: 'online',
    lastSeen: new Date(Date.now() - 5 * 60 * 1000),
    permissions: ['vote_on_decisions', 'manage_vehicles'],
  },
  {
    id: '3',
    name: 'Anna Rodriguez',
    role: 'route_planner',
    email: 'anna.rodriguez@company.com',
    status: 'away',
    lastSeen: new Date(Date.now() - 30 * 60 * 1000),
    permissions: ['vote_on_decisions', 'create_routes'],
  },
  {
    id: '4',
    name: 'David Park',
    role: 'analyst',
    email: 'david.park@company.com',
    status: 'offline',
    lastSeen: new Date(Date.now() - 2 * 60 * 60 * 1000),
    permissions: ['view_analytics', 'vote_on_decisions'],
  },
  {
    id: '5',
    name: 'Lisa Thompson',
    role: 'client',
    email: 'lisa.thompson@client.com',
    status: 'online',
    lastSeen: new Date(Date.now() - 10 * 60 * 1000),
    permissions: ['view_progress', 'comment_decisions'],
  },
];

const mockDecisions: Decision[] = [
  {
    id: '1',
    title: 'Route Optimization Algorithm Update',
    description: 'Proposal to implement new AI-powered route optimization algorithm that could reduce fuel costs by 15%.',
    type: 'route_optimization',
    status: 'voting',
    createdBy: '1',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    deadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    votes: [
      { stakeholderId: '1', decision: 'approve', comment: 'Great potential for cost savings', timestamp: new Date() },
      { stakeholderId: '2', decision: 'approve', timestamp: new Date() },
    ],
    comments: [],
    requiredApprovals: 3,
    priority: 'high',
  },
  {
    id: '2',
    title: 'Electric Vehicle Fleet Expansion',
    description: 'Proposal to add 5 electric delivery vehicles to reduce carbon footprint by 30%.',
    type: 'vehicle_assignment',
    status: 'pending',
    createdBy: '2',
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    votes: [],
    comments: [],
    requiredApprovals: 4,
    priority: 'medium',
  },
];

const mockNotifications: Notification[] = [
  {
    id: '1',
    type: 'vote_required',
    title: 'Vote Required',
    message: 'Route Optimization Algorithm Update requires your vote',
    timestamp: new Date(Date.now() - 30 * 60 * 1000),
    read: false,
    actionRequired: true,
  },
  {
    id: '2',
    type: 'deadline_approaching',
    title: 'Deadline Approaching',
    message: 'Decision deadline in 3 days',
    timestamp: new Date(Date.now() - 60 * 60 * 1000),
    read: false,
    actionRequired: true,
  },
];

const getRoleIcon = (role: Stakeholder['role']) => {
  switch (role) {
    case 'project_manager': return Users;
    case 'fleet_manager': return Truck;
    case 'route_planner': return MapPin;
    case 'analyst': return BarChart3;
    case 'client': return Users;
    case 'admin': return Users;
    default: return Users;
  }
};

const getRoleColor = (role: Stakeholder['role']) => {
  switch (role) {
    case 'project_manager': return 'text-purple-600 bg-purple-100';
    case 'fleet_manager': return 'text-blue-600 bg-blue-100';
    case 'route_planner': return 'text-green-600 bg-green-100';
    case 'analyst': return 'text-orange-600 bg-orange-100';
    case 'client': return 'text-pink-600 bg-pink-100';
    case 'admin': return 'text-gray-600 bg-gray-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

const getStatusColor = (status: Stakeholder['status']) => {
  switch (status) {
    case 'online': return 'bg-green-500';
    case 'away': return 'bg-yellow-500';
    case 'offline': return 'bg-gray-400';
    default: return 'bg-gray-400';
  }
};

const getPriorityColor = (priority: Decision['priority']) => {
  switch (priority) {
    case 'critical': return 'text-red-600 bg-red-100';
    case 'high': return 'text-orange-600 bg-orange-100';
    case 'medium': return 'text-blue-600 bg-blue-100';
    case 'low': return 'text-gray-600 bg-gray-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

export const StakeholderInterface: React.FC<StakeholderInterfaceProps> = ({
  className,
  enableRealTimeUpdates = true,
}) => {
  const { currentProject } = useProject();
  const [activeTab, setActiveTab] = useState<'overview' | 'decisions' | 'stakeholders' | 'notifications'>('overview');
  const [selectedDecision, setSelectedDecision] = useState<Decision | null>(null);
  const [showVoteDialog, setShowVoteDialog] = useState(false);
  const [newComment, setNewComment] = useState('');

  // Calculate collaboration metrics
  const collaborationMetrics = useMemo(() => {
    const activeStakeholders = mockStakeholders.filter(s => s.status === 'online').length;
    const pendingDecisions = mockDecisions.filter(d => d.status === 'voting' || d.status === 'pending').length;
    const unreadNotifications = mockNotifications.filter(n => !n.read).length;
    const avgResponseTime = 2.5; // hours - would be calculated from actual data
    
    return {
      activeStakeholders,
      totalStakeholders: mockStakeholders.length,
      pendingDecisions,
      totalDecisions: mockDecisions.length,
      unreadNotifications,
      avgResponseTime,
      collaborationScore: Math.round((activeStakeholders / mockStakeholders.length) * 100),
    };
  }, []);

  const StakeholderCard: React.FC<{ stakeholder: Stakeholder }> = ({ stakeholder }) => {
    const RoleIcon = getRoleIcon(stakeholder.role);
    
    return (
      <MagicCard className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <RoleIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div className={cn(
                'absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white',
                getStatusColor(stakeholder.status)
              )} />
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900">{stakeholder.name}</h4>
              <div className="flex items-center space-x-2">
                <span className={cn(
                  'text-xs px-2 py-1 rounded-full font-medium',
                  getRoleColor(stakeholder.role)
                )}>
                  {stakeholder.role.replace('_', ' ')}
                </span>
                <span className="text-xs text-gray-500">
                  {stakeholder.status === 'online' ? 'Online' : 
                   stakeholder.status === 'away' ? 'Away' : 
                   `Last seen ${Math.round((Date.now() - stakeholder.lastSeen.getTime()) / (1000 * 60))}m ago`}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            <button className="p-1 hover:bg-gray-100 rounded">
              <MessageSquare className="w-4 h-4 text-gray-500" />
            </button>
            <button className="p-1 hover:bg-gray-100 rounded">
              <MoreHorizontal className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      </MagicCard>
    );
  };

  const DecisionCard: React.FC<{ decision: Decision }> = ({ decision }) => {
    const approvals = decision.votes.filter(v => v.decision === 'approve').length;
    const rejections = decision.votes.filter(v => v.decision === 'reject').length;
    const progressPercentage = (approvals / decision.requiredApprovals) * 100;
    
    return (
      <MagicCard className="p-6 cursor-pointer hover:shadow-lg transition-shadow">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{decision.title}</h3>
              <p className="text-sm text-gray-600 mt-1">{decision.description}</p>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className={cn(
                'text-xs px-2 py-1 rounded-full font-medium',
                getPriorityColor(decision.priority)
              )}>
                {decision.priority}
              </span>
              
              <span className={cn(
                'text-xs px-2 py-1 rounded-full font-medium',
                decision.status === 'voting' ? 'text-blue-600 bg-blue-100' :
                decision.status === 'approved' ? 'text-green-600 bg-green-100' :
                decision.status === 'rejected' ? 'text-red-600 bg-red-100' :
                'text-gray-600 bg-gray-100'
              )}>
                {decision.status}
              </span>
            </div>
          </div>
          
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Approval Progress</span>
              <span className="font-medium">{approvals}/{decision.requiredApprovals}</span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className="bg-green-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progressPercentage, 100)}%` }}
                transition={{ duration: 1 }}
              />
            </div>
            
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{approvals} approvals, {rejections} rejections</span>
              <span>Due {new Date(decision.deadline).toLocaleDateString()}</span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center space-x-4">
              <button className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600">
                <Eye className="w-4 h-4" />
                <span>View Details</span>
              </button>
              
              <button className="flex items-center space-x-1 text-sm text-gray-600 hover:text-green-600">
                <MessageCircle className="w-4 h-4" />
                <span>{decision.comments.length} comments</span>
              </button>
            </div>
            
            {decision.status === 'voting' && (
              <div className="flex items-center space-x-2">
                <button
                  className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDecision(decision);
                    setShowVoteDialog(true);
                  }}
                >
                  <ThumbsUp className="w-4 h-4" />
                  <span>Vote</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </MagicCard>
    );
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-blue-100">
            <Users className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Stakeholder Collaboration</h2>
            <p className="text-gray-600">
              Multi-stakeholder planning for {currentProject?.name || 'Project'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {enableRealTimeUpdates && (
            <motion.div
              className="flex items-center space-x-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span>Live Updates</span>
            </motion.div>
          )}
          
          <button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
            <UserPlus className="w-4 h-4" />
            <span>Invite Stakeholder</span>
          </button>
        </div>
      </div>

      {/* Collaboration Metrics */}
      <BentoGrid columns={{ sm: 2, md: 4, lg: 4 }} gap={4}>
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <NumberTicker
              value={collaborationMetrics.activeStakeholders}
              className="text-xl font-bold text-blue-600"
              duration={1.5}
            />
            <p className="text-xs text-gray-600">Active Now</p>
          </MagicCard>
        </BentoGridItem>
        
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Vote className="w-6 h-6 text-orange-600 mx-auto mb-2" />
            <NumberTicker
              value={collaborationMetrics.pendingDecisions}
              className="text-xl font-bold text-orange-600"
              duration={1.7}
            />
            <p className="text-xs text-gray-600">Pending Decisions</p>
          </MagicCard>
        </BentoGridItem>
        
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Bell className="w-6 h-6 text-red-600 mx-auto mb-2" />
            <NumberTicker
              value={collaborationMetrics.unreadNotifications}
              className="text-xl font-bold text-red-600"
              duration={1.9}
            />
            <p className="text-xs text-gray-600">Unread Alerts</p>
          </MagicCard>
        </BentoGridItem>
        
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Clock className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <NumberTicker
              value={collaborationMetrics.avgResponseTime}
              suffix="h"
              className="text-xl font-bold text-green-600"
              duration={2.1}
            />
            <p className="text-xs text-gray-600">Avg Response</p>
          </MagicCard>
        </BentoGridItem>
      </BentoGrid>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        {[
          { key: 'overview', label: 'Overview', icon: Eye },
          { key: 'decisions', label: 'Decisions', icon: Vote },
          { key: 'stakeholders', label: 'Stakeholders', icon: Users },
          { key: 'notifications', label: 'Notifications', icon: Bell },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key as any)}
            className={cn(
              'flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors flex-1 justify-center',
              activeTab === key
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <BentoGrid columns={{ sm: 1, md: 2 }} gap={6}>
                <BentoGridItem>
                  <MagicCard className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                    <div className="space-y-3">
                      {mockDecisions.slice(0, 3).map(decision => (
                        <div key={decision.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <Vote className="w-5 h-5 text-blue-600" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{decision.title}</p>
                            <p className="text-xs text-gray-600">
                              {decision.votes.length} votes • Due {new Date(decision.deadline).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </MagicCard>
                </BentoGridItem>
                
                <BentoGridItem>
                  <MagicCard className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Collaboration Score</h3>
                    <div className="text-center">
                      <NumberTicker
                        value={collaborationMetrics.collaborationScore}
                        suffix="/100"
                        className="text-3xl font-bold text-blue-600"
                        duration={2.5}
                      />
                      <p className="text-sm text-gray-600 mt-2">
                        {collaborationMetrics.activeStakeholders} of {collaborationMetrics.totalStakeholders} stakeholders active
                      </p>
                    </div>
                  </MagicCard>
                </BentoGridItem>
              </BentoGrid>
            </div>
          )}

          {activeTab === 'decisions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Active Decisions</h3>
                <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  <Vote className="w-4 h-4" />
                  <span>New Decision</span>
                </button>
              </div>
              
              <div className="space-y-4">
                {mockDecisions.map(decision => (
                  <DecisionCard key={decision.id} decision={decision} />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'stakeholders' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Project Stakeholders</h3>
                <div className="flex items-center space-x-2">
                  <button className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <Filter className="w-4 h-4" />
                    <span>Filter</span>
                  </button>
                  <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                    <UserPlus className="w-4 h-4" />
                    <span>Add Stakeholder</span>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mockStakeholders.map(stakeholder => (
                  <StakeholderCard key={stakeholder.id} stakeholder={stakeholder} />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <button className="text-sm text-blue-600 hover:text-blue-700">
                  Mark all as read
                </button>
              </div>
              
              <div className="space-y-3">
                {mockNotifications.map(notification => (
                  <MagicCard key={notification.id} className={cn(
                    'p-4',
                    !notification.read && 'border-l-4 border-blue-500 bg-blue-50'
                  )}>
                    <div className="flex items-start space-x-3">
                      <div className={cn(
                        'p-2 rounded-lg',
                        notification.actionRequired ? 'bg-red-100' : 'bg-blue-100'
                      )}>
                        <Bell className={cn(
                          'w-4 h-4',
                          notification.actionRequired ? 'text-red-600' : 'text-blue-600'
                        )} />
                      </div>
                      
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{notification.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(notification.timestamp).toLocaleString()}
                        </p>
                      </div>
                      
                      {notification.actionRequired && (
                        <button className="text-sm bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors">
                          Take Action
                        </button>
                      )}
                    </div>
                  </MagicCard>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Vote Dialog */}
      {showVoteDialog && selectedDecision && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Vote on: {selectedDecision.title}
            </h3>
            
            <div className="space-y-4">
              <div className="flex space-x-3">
                <button className="flex-1 flex items-center justify-center space-x-2 p-3 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors">
                  <ThumbsUp className="w-5 h-5" />
                  <span>Approve</span>
                </button>
                
                <button className="flex-1 flex items-center justify-center space-x-2 p-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors">
                  <ThumbsDown className="w-5 h-5" />
                  <span>Reject</span>
                </button>
              </div>
              
              <textarea
                placeholder="Add a comment (optional)"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowVoteDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Submit Vote
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};